/* 0.48.22 - A Repeating display's "Show only rows where".
 *
 * Each condition names a column of the row (a path such as
 * nature.code, or a list path such as goods[].class), a test and,
 * for most tests, a value. The value is resolved by the caller first,
 * so it can be fixed text, a {field} or variable, or a formula.
 *
 *   equals / not_equals   the same text, ignoring case and spaces at the
 *                         ends; two numbers compare as numbers ("5" and
 *                         "5.0" are equal)
 *   contains              the value is part of it, ignoring case
 *   one_of                equals one of the values, comma-separated
 *   empty / not_empty     no value at all / some value
 *   gt / lt               more / less than: numbers when both are
 *                         numbers, else as text (so ISO dates such as
 *                         2026-10-27 compare in date order)
 *
 * A column holding a list passes when any of its items passes
 * (not_equals and empty: when none of them is the value / all are
 * empty). An empty value from the page makes a condition that needs
 * one pass, so a filter on a dropdown shows every row until something
 * is picked.
 */

const NEEDS_VALUE = new Set(['equals', 'not_equals', 'contains', 'one_of', 'gt', 'lt']);
export const ROW_FILTER_OPS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'one_of', label: 'is one of (comma-separated)' },
  { value: 'empty', label: 'is empty' },
  { value: 'not_empty', label: 'is not empty' },
  { value: 'gt', label: 'is more than' },
  { value: 'lt', label: 'is less than' },
];
export const rowFilterNeedsValue = (op) => NEEDS_VALUE.has(op);

const norm = (v) => (v === null || v === undefined ? '' : String(v)).trim();
const asNum = (s) => (s !== '' && /^-?\d+(\.\d+)?$/.test(s.replace(/[\s,]/g, '')) ? Number(s.replace(/[\s,]/g, '')) : null);
const same = (a, b) => {
  const x = norm(a); const y = norm(b);
  const nx = asNum(x); const ny = asNum(y);
  if (nx !== null && ny !== null) return nx === ny;
  return x.toLowerCase() === y.toLowerCase();
};
const cmp = (a, b) => {
  const x = norm(a); const y = norm(b);
  const nx = asNum(x); const ny = asNum(y);
  if (nx !== null && ny !== null) return nx - ny;
  return x < y ? -1 : x > y ? 1 : 0;
};

function testOne(v, op, want) {
  switch (op) {
    case 'equals': return same(v, want);
    case 'not_equals': return !same(v, want);
    case 'contains': return norm(v).toLowerCase().includes(norm(want).toLowerCase());
    case 'one_of': return String(want).split(',').map(s => s.trim()).filter(Boolean).some(w => same(v, w));
    case 'empty': return norm(v) === '';
    case 'not_empty': return norm(v) !== '';
    case 'gt': return norm(v) !== '' && cmp(v, want) > 0;
    case 'lt': return norm(v) !== '' && cmp(v, want) < 0;
    default: return true;
  }
}

function passes(row, f, readValue, resolveValue) {
  const op = f.op || 'equals';
  const column = String(f.column || '').trim();
  if (!column) return true;
  let want = '';
  if (NEEDS_VALUE.has(op)) {
    want = norm(resolveValue ? resolveValue(f.value) : f.value);
    if (want === '') return true;
  }
  const raw = readValue(row, column);
  const items = Array.isArray(raw) ? raw.flat(Infinity) : [raw];
  if (op === 'not_equals') return items.every(v => testOne(v, op, want));
  if (op === 'empty') return items.every(v => testOne(v, op, want));
  return items.some(v => testOne(v, op, want));
}

export function filterRows(rows, filters, mode, readValue, resolveValue) {
  const list = (Array.isArray(filters) ? filters : []).filter(f => f && String(f.column || '').trim());
  if (!list.length || !Array.isArray(rows)) return rows;
  const any = mode === 'any';
  return rows.filter(row => (any
    ? list.some(f => passes(row, f, readValue, resolveValue))
    : list.every(f => passes(row, f, readValue, resolveValue))));
}
